/**
 * Governance Rules — Static Data
 *
 * All validation rules, rulebook definitions, pipeline file references,
 * and stage/severity configuration for the GovernanceRulesTab.
 *
 * Canonical location: portals/admin/content/governance/ (DDD — UI in portal)
 */

import { DEFAULT_POLICY } from '../../../../governance'

// ─── Types ────────────────────────────────────────────────────

/**
 * Severity — Admin rules-tab vocabulary (uppercase, doc-only).
 *
 * Phase 1 (Strak-Trek): Extended to the 5-level model so admins can filter
 * regels op semantische ernst. NOTE: these labels are PURE DOCUMENTATIE in
 * the rules tab — ze beïnvloeden GEEN pipeline-gate of verdict. Of een
 * regel daadwerkelijk blokkeert wordt bepaald door de stage-implementatie
 * (kind='blocker' vs 'coach' op de ValidationIssue), niet door dit label.
 * Het label hier helpt admins regels te groeperen op type-risico.
 *
 * Display mapping (used by SEVERITY_CONFIG in GovernanceRulesCards.tsx):
 *   CRITICAL → 🔴 Kritiek   student-facing risk (antwoord-lek, indicator-leak)
 *   BLOCKER  → 🟠 Blocker   pedagogisch onjuist of structureel ongeldig
 *   REVIEW   → 🟡 Review    heuristisch signaal, vraagt menselijk oordeel
 *   WARNING  → 🔵 Coach     verbeterpunt voor auteur (legacy label)
 *   INFO     → ⚪ Info      observatie zonder actie
 */
export type Severity = 'CRITICAL' | 'BLOCKER' | 'REVIEW' | 'WARNING' | 'INFO'

export type Stage =
  | 'schema'
  | 'type'
  | 'content'
  | 'pedagogy'
  | 'support'
  | 'familyStats'
  | 'diversity'
  | 'visual'
  | 'graph'
  | 'seed'
  | 'policy'

export interface Rule {
  id: string
  label: string
  description: string
  severity: Severity
  stage: Stage
  category: string
  example?: string
  fix?: string
}

export type ViewId =
  | 'regels'
  | 'regelboeken'
  | 'family-hints'
  | 'drempelwaarden'

export interface PipelineFile {
  name: string
  path: string
  stage: Stage
  description: string
}

// ─── Stage Config ─────────────────────────────────────────────

export const STAGE_LABELS: Record<Stage, string> = {
  schema: 'Schema',
  type: 'Vraagtype',
  content: 'Inhoud',
  pedagogy: 'Pedagogie',
  support: 'Ondersteuning',
  familyStats: 'Family-statistiek',
  diversity: 'Vraagtype-diversiteit',
  visual: 'Visueel',
  graph: 'LDG-Graaf',
  seed: 'Seed-integriteit',
  policy: 'Beleid',
}

export const STAGE_COLORS: Record<Stage, string> = {
  schema: 'bg-slate-100 text-slate-700',
  type: 'bg-blue-100 text-blue-700',
  content: 'bg-violet-100 text-violet-700',
  pedagogy: 'bg-purple-100 text-purple-700',
  support: 'bg-teal-100 text-teal-700',
  familyStats: 'bg-cyan-100 text-cyan-700',
  diversity: 'bg-emerald-100 text-emerald-700',
  visual: 'bg-orange-100 text-orange-700',
  graph: 'bg-pink-100 text-pink-700',
  seed: 'bg-lime-100 text-lime-700',
  policy: 'bg-amber-100 text-amber-700',
}

export const ALL_STAGES: (Stage | 'all')[] = [
  'all',
  'schema',
  'type',
  'content',
  'pedagogy',
  'support',
  'familyStats',
  'diversity',
  'visual',
  'graph',
  'seed',
  'policy',
]

// ─── Validation Rules ─────────────────────────────────────────

export const RULES: Rule[] = [
  // Schema Stage
  {
    id: 'SCH-001',
    label: 'Verplichte velden aanwezig',
    description:
      'Elke vraag moet de velden id, familyId, module, skill, subSkill, tier, levelCode, group, question, options, correctAnswer en feedback bevatten.',
    severity: 'BLOCKER',
    stage: 'schema',
    category: 'Structuur',
    example: 'Zie: src/governance/pipeline/stages/schemaStage.ts',
    fix: 'Voeg de ontbrekende verplichte velden toe aan de vraagdefinitie.',
  },
  {
    id: 'SCH-002',
    label: 'Uniek vraag-ID',
    description: 'Elk vraag-ID moet uniek zijn binnen het volledige systeem.',
    severity: 'BLOCKER',
    stage: 'schema',
    category: 'Structuur',
    fix: 'Gebruik het naamgevingsschema: [FAMILIE]-[NUMMER] (bijv. GET-BREUK-BASIS-001).',
  },
  {
    id: 'SCH-003',
    label: 'Geldige tier (1–16)',
    description: 'Tier moet een getal zijn tussen 1 en 16.',
    severity: 'BLOCKER',
    stage: 'schema',
    category: 'Metadata',
    fix: 'Pas de tier aan naar een waarde tussen 1 en 16.',
  },
  {
    id: 'SCH-004',
    label: 'Geldig groepsjaar (6, 7 of 8)',
    description: 'Het veld "group" moet 6, 7 of 8 zijn.',
    severity: 'BLOCKER',
    stage: 'schema',
    category: 'Metadata',
    fix: 'Stel group in op 6, 7 of 8.',
  },
  // Type Stage
  {
    id: 'TYP-001',
    label: 'Geldig vraagtype',
    description:
      'Het vraagtype moet geregistreerd zijn in het vraagtype-register (questionTypeRegistry.ts). Er zijn 29 geregistreerde vraagtypes.',
    severity: 'BLOCKER',
    stage: 'type',
    category: 'Vraagtype',
    example:
      'Geldige typen: multiple_choice, fill_in, ordering, matching, drag_drop_fill, table_reading, number_sequence, ratio_table, text_highlight, bar_chart, line_chart, pie_chart, schedule_reading, cijferend_rekenen, cijferend_delen, clock, clock_set, money, multiplication_grid, true_false_series, estimation_slider, categorize, error_spotting, grid_shade, step_solver, sentence_parser, comparison_judge, verb_conjugation_flow, thermometer_slider, sentence_error_judge',
    fix: 'Gebruik een van de geregistreerde vraagtypes.',
  },
  {
    id: 'TYP-002',
    label: 'Antwoordformaat klopt bij type',
    description:
      'Het antwoordobject moet overeenkomen met het verwachte formaat voor het vraagtype.',
    severity: 'BLOCKER',
    stage: 'type',
    category: 'Vraagtype',
    fix: 'Raadpleeg de vraagtype-specificatie voor het juiste antwoordformaat.',
  },
  {
    id: 'TYP-003',
    label: 'Multiple choice: 2–5 opties',
    description:
      'Meerkeuze-vragen moeten minimaal 2 en maximaal 5 antwoordopties bevatten.',
    severity: 'WARNING',
    stage: 'type',
    category: 'Vraagtype',
    fix: 'Voeg meer antwoordopties toe of verander het vraagtype.',
  },
  // Content Stage
  {
    id: 'CON-001',
    label: 'Vraagstelling niet leeg',
    description:
      'De vraagstelling mag niet leeg zijn en moet minimaal 10 tekens bevatten.',
    severity: 'BLOCKER',
    stage: 'content',
    category: 'Inhoud',
    fix: 'Voeg een duidelijke vraagstelling toe.',
  },
  {
    id: 'CON-002',
    label: 'Uitleg aanwezig (explanationText)',
    description:
      'feedback.explanationText is altijd verplicht. Minimale lengte afhankelijk van tier-band.',
    severity: 'BLOCKER',
    stage: 'content',
    category: 'Feedback',
    example:
      'Minimale lengte: basic=30, intermediate=50, advanced=80, expert=100 tekens',
    fix: 'Voeg een stap-voor-stap uitleg toe in feedback.explanationText.',
  },
  {
    id: 'CON-003',
    label: 'Distractor-uitleg voor alle opties',
    description:
      'feedback.distractorExplanations moet een uitleg bevatten voor elke antwoordoptie.',
    severity: 'BLOCKER',
    stage: 'content',
    category: 'Feedback',
    fix: 'Voeg een uitleg toe voor elke optie (index 0, 1, 2, 3).',
  },
  {
    id: 'CON-004',
    label: 'Geen verboden vocabulaire voor tier-band',
    description:
      'Vragen op basic-niveau mogen geen technische termen bevatten zoals "notatie", "commutatief", "proportioneel".',
    severity: 'WARNING',
    stage: 'content',
    category: 'Taalgebruik',
    fix: 'Herformuleer de vraag met eenvoudiger taalgebruik.',
  },
  {
    id: 'CON-005',
    label: 'Vraaglengte binnen budget',
    description: 'De vraagstelling mag niet te lang zijn voor het tier-niveau.',
    severity: 'WARNING',
    stage: 'content',
    category: 'Taalgebruik',
    example:
      'Max: basic=200, intermediate=400, advanced=600, expert=800 tekens',
    fix: 'Verkort de vraagstelling.',
  },
  {
    id: 'CON-006',
    label: 'Numerieke schaal-mismatch distractors',
    description:
      'Bij numerieke meerkeuze-vragen moeten distractors in hetzelfde bereik liggen als het correcte antwoord. Kinderen kunnen anders op grootte alleen al gokken (bijv. antwoord=18, distractors allemaal ~6).',
    severity: 'REVIEW',
    stage: 'content',
    category: 'Distractors',
    example:
      'Fout: antwoord €60, distractors €6, €5,50, €6,50. Goed: antwoord €60, distractors €57,60, €38,40, €68.',
    fix: 'Maak distractors die plausibele rekenfouten vertegenwoordigen op dezelfde schaal als het correcte antwoord.',
  },
  {
    id: 'CON-007',
    label: 'Visueel onmogelijke spelling-distractors',
    description:
      'Bij taalvragen mogen distractors geen visueel onmogelijke patronen bevatten die kinderen direct kunnen uitsluiten: gemengde hoofdletters (AmsterdaM), driedubbele d/t-clusters (hooftdt), of onmogelijke apostrofposities.',
    severity: 'REVIEW',
    stage: 'content',
    category: 'Distractors',
    example:
      'Fout: "AmsterdaM", "hooftdt", "mond\'t". Goed: "Amsterdan", "hooft", "mondt".',
    fix: 'Gebruik distractors die echte spelfouten of misconcepties vertegenwoordigen — geen onmogelijke lettercombinaties.',
  },
  {
    id: 'CON-008',
    label: 'Correct antwoord valt op tussen distractors',
    description:
      'Het correcte antwoord mag niet visueel opvallen tussen de distractors. Detecteert: lengte-outlier (correct veel korter/langer), numerieke spreiding (alle distractors aan dezelfde kant), format-verschil (correct heeft ander format), en distractor-clustering (distractors lijken sterk op elkaar maar niet op correct).',
    severity: 'REVIEW',
    stage: 'content',
    category: 'Distractors',
    example:
      'Fout: correct="€57,60" (7 tekens), distractors="Dat kan niet berekend worden" (28 tekens), "Het hangt af van de winkel" (25 tekens), "Onbekend" (8 tekens). Goed: alle opties in vergelijkbaar format en lengte.',
    fix: 'Zorg dat het correcte antwoord qua lengte, format, numeriek bereik en visuele gelijkenis opgaat in de distractors. Varieer distractors zodat ze niet allemaal op elkaar lijken.',
  },
  // Pedagogy Stage (3b)
  {
    id: 'PED-001',
    label: 'DOK-tier afstemming',
    description:
      'Het DOK-niveau (Depth of Knowledge) van de vraag moet passen bij de tier-band. Expert-tiers (13-16) vereisen minimaal DOK 3, advanced (9-12) minimaal DOK 2.',
    severity: 'WARNING',
    stage: 'pedagogy',
    category: 'Pedagogisch Contract',
    example: 'Tier 14 (expert) met DOK 1 (recall) → te laag',
    fix: 'Verhoog de cognitieve complexiteit van de vraag of pas de tier aan.',
  },
  {
    id: 'PED-002',
    label: 'Distractor misconceptie-tagging',
    description:
      'MC-distractors moeten getagd zijn met misconceptie-IDs uit het misconceptie-register. Verplicht voor tier ≥ 9.',
    severity: 'WARNING',
    stage: 'pedagogy',
    category: 'Pedagogisch Contract',
    fix: 'Voeg misconceptionTags toe aan het pedagogyContract van de vraag.',
  },
  {
    id: 'PED-003',
    label: 'Indicator-antwoord pariteit',
    description:
      'Visual indicator-waarden (indicatorValue) mogen het correcte antwoord niet lekken. Een indicator die binnen 5% van het antwoord ligt, wordt geblokkeerd.',
    severity: 'CRITICAL',
    stage: 'pedagogy',
    category: 'Pedagogisch Contract',
    fix: 'Verwijder of wijzig de indicatorValue zodat deze niet het antwoord onthult.',
  },
  {
    id: 'PED-004',
    label: 'Gestructureerde data aanwezig',
    description:
      'Rekenkundige vraagtypes (cijferend_rekenen, cijferend_delen) moeten gestructureerde data bevatten in question.data in plaats van regex-parsing uit de vraagtekst.',
    severity: 'WARNING',
    stage: 'pedagogy',
    category: 'Pedagogisch Contract',
    fix: 'Voeg columnArithmetic of longDivision data toe aan het data-object van de vraag.',
  },
  // Diversity Stage (5c) — QUESTION_TYPE_DIVERSITY_RULES
  // P1-3 (mei 2026): diversityStage emit nu onder eigen 'diversity' domain
  // (was 'support'). Het Rules-tab label volgt diezelfde split.
  {
    id: 'DIV-001',
    label: 'Minimaal 3 vraagtypes per family',
    description:
      'Elke family moet minimaal 3 verschillende vraagtypes bevatten om monotone oefensessies te voorkomen.',
    severity: 'WARNING',
    stage: 'diversity',
    category: 'Type-diversiteit',
    example:
      'Fout: family met alleen MC + fill_in (2 types). Goed: MC + fill_in + error_spotting (3 types).',
    fix: 'Voeg interactieve vraagtypes toe zoals error_spotting, true_false_series, categorize, ordering, of step_solver.',
  },
  {
    id: 'DIV-002',
    label: 'Minimaal 1 interactief type',
    description:
      'Elke family moet minimaal 1 interactief vraagtype bevatten (niet MC of fill_in) voor hogere cognitieve activatie.',
    severity: 'WARNING',
    stage: 'diversity',
    category: 'Type-diversiteit',
    fix: 'Voeg een interactief type toe: ordering, matching, categorize, error_spotting, true_false_series, step_solver, of comparison_judge.',
  },
  {
    id: 'DIV-003',
    label: 'Type-overuse per family (rollup)',
    description:
      'Lage-cognitie vraagtypes mogen niet doorslaan binnen een family. Per type geldt een eigen plafond: multiple_choice ≤ 50%, fill_in ≤ 30%, true_false_series ≤ 30%. Eén code (DIVERSITY_TYPE_OVERUSE) bundelt alle drempels; metadata.reason geeft het overschreden type.',
    severity: 'WARNING',
    stage: 'diversity',
    category: 'Type-diversiteit',
    example:
      'Fout: 6 van 8 vragen zijn MC (75% > 50%). Fout: 4 van 10 vragen zijn fill_in (40% > 30%).',
    fix: 'Vervang het overschreden type door interactievere varianten (step_solver, matching, error_spotting, categorize). Drempels staan in TYPE_OVERUSE_THRESHOLDS in diversityStage.ts.',
  },
  {
    id: 'DIV-004',
    label: 'Grote families: minimaal 4 types',
    description:
      'Families met 8 of meer vragen zouden minimaal 4 verschillende vraagtypes moeten bevatten.',
    severity: 'WARNING',
    stage: 'diversity',
    category: 'Type-diversiteit',
    fix: 'Voeg meer variatie toe — grotere families bieden ruimte voor diverse interacties.',
  },
  {
    id: 'DIV-005',
    label: 'DOK3-capabel vraagtype aanbevolen',
    description:
      'Families met 5+ vragen zouden minimaal 1 DOK3-capabel vraagtype moeten bevatten. De DOK3-set wordt automatisch afgeleid uit cognitiveCeiling === "DOK3" in questionTypeDefinitions.ts, zodat nieuwe DOK3-types automatisch meetellen zonder code-wijziging hier.',
    severity: 'INFO',
    stage: 'diversity',
    category: 'Type-diversiteit',
    fix: 'Voeg een analytisch type toe (bv. error_spotting, categorize, step_solver, comparison_judge, sentence_parser, sentence_error_judge, true_false_series).',
  },
  // DIV-006 verwijderd in Sprint 7: was false signal — controleerde seed-JSON volgorde,
  // maar de adaptive engine (v11.2) en modeSelectors shuffle + selecteren runtime.
  // DIV-007 samengevoegd met DIV-003 als DIVERSITY_TYPE_OVERUSE rollup.
  // Seed Stage (5b)
  {
    id: 'SEED-001',
    label: 'Tier/skill auto-correctie',
    description:
      'De seedConsolidator heeft de tier, skill of subSkill automatisch gecorrigeerd op basis van de taxonomy-definitie. Dit duidt op een mismatch in de seed-data.',
    severity: 'REVIEW',
    stage: 'seed',
    category: 'Seed-integriteit',
    fix: 'Corrigeer de tier/skill/subSkill in het seed-bestand zodat deze overeenkomen met de taxonomy.',
  },
  {
    id: 'SEED-002',
    label: 'FamilyId niet gevonden in taxonomy',
    description:
      'De familyId van de vraag komt niet voor in de taxonomy-registry.',
    severity: 'BLOCKER',
    stage: 'seed',
    category: 'Seed-integriteit',
    fix: 'Controleer de familyId en voeg deze toe aan de taxonomy als deze ontbreekt.',
  },
  {
    id: 'SEED-003',
    label: 'pedagogy.dok ontbreekt in seed',
    description:
      'Elke vraag moet een DOK-niveau (1-4) declareren in pedagogy.dok. Bij ontbrekend DOK draait de dokAutoTagger een heuristische schatting die als suggestie wordt aangeboden. Bij actieve enforcePedagogicalContract-vlag is dit een blocker; anders een coach-bevinding.',
    severity: 'BLOCKER',
    stage: 'seed',
    category: 'Seed-integriteit',
    fix: 'Voeg een pedagogy.dok waarde (1-4) toe aan de seed-JSON. De heuristische suggestie (dokAutoTagger) zit in de metadata van het SEED_DOK_MISSING-issue en kan als startpunt worden gebruikt.',
  },
  // Support Stage — see src/governance/pipeline/stages/supportStage.ts
  {
    id: 'SUP-001',
    label: 'Uitleg-kwaliteit (aanwezig en lengte)',
    description:
      'Rollup-regel: pipeline emit SUPPORT_EXPLANATION_QUALITY met metadata.reason = missing (uitleg ontbreekt, blocker), too_short (onder MIN_EXPLANATION_CHARS, coach) of too_long (boven MAX_EXPLANATION_CHARS, info). Drempels: SUPPORT_THRESHOLDS in src/support/policy.ts.',
    severity: 'BLOCKER',
    stage: 'support',
    category: 'Ondersteuning',
    example:
      'reason=missing -> blocker · reason=too_short -> coach · reason=too_long -> info',
    fix: 'Schrijf een defaultExplanation tussen MIN- en MAX_EXPLANATION_CHARS die de aanpak beschrijft, niet alleen het antwoord.',
  },
  {
    id: 'SUP-002',
    label: 'CITO-spreiding binnen family',
    description:
      'Rollup-regel: vanaf MIN_FAMILY_SIZE_FOR_CITO_SPREAD vragen wordt gecontroleerd of citoDifficulty varieert. Pipeline emit SUPPORT_FAMILY_CITO_SPREAD met metadata.reason = no_spread (alle waarden identiek, review) of low_spread (onder MIN_CITO_SPREAD, coach). P1-2 (mei 2026): domain = familyStats (was support).',
    severity: 'REVIEW',
    stage: 'familyStats',
    category: 'Family-statistiek',
    example:
      'reason=no_spread -> alle CITO-waarden gelijk · reason=low_spread -> spread onder drempel',
    fix: 'Varieer citoDifficulty over de vragen in de family zodat het adaptieve algoritme passende moeilijkheid kan kiezen.',
  },
  {
    id: 'SUP-003',
    label: 'Family-hint registratie (verplichte families)',
    description:
      'Families met hintsRequired=true in support-policy moeten in FAMILY_SUPPORT_REGISTRY staan met minimaal 1 hint. Pipeline emit SUPPORT_FAMILY_NOT_IN_REGISTRY (blocker) of SUPPORT_HINTS_EMPTY (blocker).',
    severity: 'BLOCKER',
    stage: 'support',
    category: 'Ondersteuning',
    fix: 'Voeg de family toe aan FAMILY_SUPPORT_REGISTRY met passende hints, of zet hintsRequired=false in de support-policy.',
  },
  {
    id: 'SUP-004',
    label: 'Voldoende vragen per family',
    description:
      'Iedere family hoort minimaal MIN_QUESTIONS_PER_FAMILY vragen te hebben voor adequate oefen-dekking. Pipeline emit SUPPORT_FAMILY_LOW_QUESTION_COUNT (info). P1-2 (mei 2026): domain = familyStats (was support).',
    severity: 'INFO',
    stage: 'familyStats',
    category: 'Family-statistiek',
    fix: 'Schrijf extra vragen voor deze family tot de drempel bereikt is.',
  },
  {
    id: 'SUP-005',
    label: 'Geen template-collapse binnen family',
    description:
      'Vanaf MIN_FAMILY_SIZE_FOR_PATTERN_ANALYSIS vragen analyseert supportStage of vragen te uniform zijn: SUPPORT_FAMILY_NUMBER_PATTERN_COLLAPSE (numerieke modules uit NUMERIC_CONTENT_MODULE_IDS — getallen variëren onvoldoende) of SUPPORT_FAMILY_LINGUISTIC_TEMPLATE_COLLAPSE (taal-modules — zinsstructuur identiek). P1-2 (mei 2026): domain = familyStats (was support).',
    severity: 'REVIEW',
    stage: 'familyStats',
    category: 'Family-statistiek',
    fix: 'Varieer getallen, contexten of zinsstructuren tussen vragen zodat de family niet als één template-instantie aanvoelt.',
  },
  // Visual Stage
  {
    id: 'VIS-001',
    label: 'Stimulus visueel aanwezig (verplichte families)',
    description:
      'Families met stimulusRequired=true moeten een stimulusAids-array hebben.',
    severity: 'BLOCKER',
    stage: 'visual',
    category: 'Visueel',
    example:
      'Zie: src/governance/pipeline/defaultPolicy.ts → visual.familyRules',
    fix: 'Voeg een stimulusAids-array toe met het juiste visuele type.',
  },
  {
    id: 'VIS-002',
    label: 'Geen antwoordlek via visual',
    description:
      'Stimulus-visuals mogen het correcte antwoord niet direct zichtbaar maken via label/value.',
    severity: 'CRITICAL',
    stage: 'visual',
    category: 'Visueel',
    fix: 'Pas het visuele hulpmiddel aan zodat het antwoord niet direct zichtbaar is.',
  },
  {
    id: 'VIS-003',
    label: 'stimulusAids/supportAids zijn arrays',
    description:
      'Als aanwezig, moeten stimulusAids en supportAids arrays zijn (gebruik [] voor "beoordeeld, geen visuals nodig").',
    severity: 'BLOCKER',
    stage: 'visual',
    category: 'Visueel',
    fix: 'Verander het veld naar een array ([] of [{...}]).',
  },
  // Graph Stage
  {
    id: 'GRA-001',
    label: 'Geen cyclische afhankelijkheden',
    description:
      'De leerpad-graaf (LDG) mag geen cyclische afhankelijkheden bevatten.',
    severity: 'BLOCKER',
    stage: 'graph',
    category: 'LDG-Graaf',
    fix: 'Verwijder de cyclische afhankelijkheid uit de prerequisite-definitie.',
  },
  {
    id: 'GRA-002',
    label: 'FamilyId bestaat in de graaf',
    description:
      'De familyId van een vraag moet geregistreerd zijn in de LDG-graaf.',
    severity: 'REVIEW',
    stage: 'graph',
    category: 'LDG-Graaf',
    fix: 'Voeg de familyId toe aan de LDG-definitie in ldgBuilder.ts.',
  },
  // Policy
  {
    id: 'POL-001',
    label: 'Minimaal aantal vragen per family',
    description: `Elke family moet minimaal ${DEFAULT_POLICY.gates.assessmentMinQuestionsPerFamily} vragen hebben voor assessment-pool.`,
    severity: 'WARNING',
    stage: 'policy',
    category: 'Beleid',
    fix: 'Voeg meer vragen toe aan de family.',
  },
  {
    id: 'POL-002',
    label: 'Kwaliteitsdrempel productie',
    description: `Vragen met score < ${DEFAULT_POLICY.gates.productionMinScore}% worden geblokkeerd voor productie.`,
    severity: 'BLOCKER',
    stage: 'policy',
    category: 'Beleid',
    fix: 'Verbeter de kwaliteit van de vraag.',
  },
  {
    id: 'POL-003',
    label: 'Kwaliteitsdrempel assessment',
    description: `Vragen met score < ${DEFAULT_POLICY.gates.assessmentMinScore}% zijn niet assessment-geschikt.`,
    severity: 'WARNING',
    stage: 'policy',
    category: 'Beleid',
    fix: 'Verbeter de kwaliteit voor assessment-gebruik.',
  },
]

// ─── Pipeline Source Files ────────────────────────────────────

export const PIPELINE_FILES: PipelineFile[] = [
  {
    name: 'Schema Stage',
    path: 'src/governance/pipeline/stages/schemaStage.ts',
    stage: 'schema',
    description:
      'Valideert JSON-structuur, verplichte velden, ID-formaat en tier-bereik.',
  },
  {
    name: 'Type Stage',
    path: 'src/governance/pipeline/stages/typeStage.ts',
    stage: 'type',
    description: 'Valideert vraagtype-registratie en antwoordformaat per type.',
  },
  {
    name: 'Content Stage',
    path: 'src/governance/pipeline/stages/contentStage.ts',
    stage: 'content',
    description:
      'Valideert uitleg, distractors, taalgebruik en lengte-budgetten.',
  },
  {
    name: 'Pedagogy Stage (3b)',
    path: 'src/governance/pipeline/stages/pedagogyStage.ts',
    stage: 'pedagogy',
    description:
      'Valideert DOK-tier afstemming, misconceptie-tagging, indicator-pariteit en gestructureerde data.',
  },
  {
    name: 'Support Stage',
    path: 'src/governance/pipeline/stages/supportStage.ts',
    stage: 'support',
    description:
      'Valideert aanwezigheid van hints en uitleg voor verplichte families.',
  },
  {
    name: 'Visual Stage',
    path: 'src/governance/pipeline/stages/visualStage.ts',
    stage: 'visual',
    description:
      'Valideert visuele hulpmiddelen, antwoordlekken en array-conventie.',
  },
  {
    name: 'Graph Stage',
    path: 'src/governance/pipeline/stages/graphStage.ts',
    stage: 'graph',
    description:
      'Valideert LDG-graaf, DAG-integriteit en cyclische afhankelijkheden.',
  },
  {
    name: 'Seed Stage',
    path: 'src/governance/pipeline/stages/seedStage.ts',
    stage: 'seed',
    description:
      'Controleert seed-consolidatie waarschuwingen: tier/skill auto-correcties en taxonomy-mismatches.',
  },
  {
    name: 'Default Policy',
    path: 'src/governance/pipeline/defaultPolicy.ts',
    stage: 'policy',
    description:
      'Alle drempelwaarden, vocabulaire-lijsten, visuele regels en feature flags.',
  },
  {
    name: 'Issue Contract',
    path: 'src/governance/pipeline/issueContract.ts',
    stage: 'policy',
    description:
      'Canoniek issue-type (ValidationIssue), gate-logica en drempelwaarden.',
  },
]

// ─── Rulebook Color Map ───────────────────────────────────────

export const RULEBOOK_COLOR_MAP: Record<
  string,
  { badge: string; border: string; bg: string }
> = {
  blue: {
    badge: 'bg-blue-100 text-blue-700',
    border: 'border-blue-200',
    bg: 'bg-blue-50',
  },
  violet: {
    badge: 'bg-violet-100 text-violet-700',
    border: 'border-violet-200',
    bg: 'bg-violet-50',
  },
  teal: {
    badge: 'bg-teal-100 text-teal-700',
    border: 'border-teal-200',
    bg: 'bg-teal-50',
  },
  amber: {
    badge: 'bg-amber-100 text-amber-700',
    border: 'border-amber-200',
    bg: 'bg-amber-50',
  },
  rose: {
    badge: 'bg-rose-100 text-rose-700',
    border: 'border-rose-200',
    bg: 'bg-rose-50',
  },
  pink: {
    badge: 'bg-pink-100 text-pink-700',
    border: 'border-pink-200',
    bg: 'bg-pink-50',
  },
  emerald: {
    badge: 'bg-emerald-100 text-emerald-700',
    border: 'border-emerald-200',
    bg: 'bg-emerald-50',
  },
  cyan: {
    badge: 'bg-cyan-100 text-cyan-700',
    border: 'border-cyan-200',
    bg: 'bg-cyan-50',
  },
  indigo: {
    badge: 'bg-indigo-100 text-indigo-700',
    border: 'border-indigo-200',
    bg: 'bg-indigo-50',
  },
  orange: {
    badge: 'bg-orange-100 text-orange-700',
    border: 'border-orange-200',
    bg: 'bg-orange-50',
  },
  yellow: {
    badge: 'bg-yellow-100 text-yellow-700',
    border: 'border-yellow-200',
    bg: 'bg-yellow-50',
  },
}
